/*
Site Header
*/

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.megaMenuOpen = false;
    this.scrollActive = false;
    this.isSearchOpen = false;
    this.isMobileMenuOpen = false;

    this.isLocaleOpen = false;

    this.invertedHeader = this.hasAttribute("data-inverted");

    this.handleHeaderClick = this.handleHeaderClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    this.addEventListener("click", this.handleHeaderClick);
    document.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("scroll", this.handleScroll, { passive: true });

    this.initMegaMenuHoverDelay();
    this.initLocaleHover();
    this.initMobileLocaleSelect();

    this.handleViewportResize = this.handleViewportResize.bind(this);
    this.setViewportUnit();
    window.addEventListener("resize", this.handleViewportResize, {
      passive: true,
    });
    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        "resize",
        this.handleViewportResize,
        {
          passive: true,
        }
      );
      window.visualViewport.addEventListener(
        "scroll",
        this.handleViewportResize,
        {
          passive: true,
        }
      );
    }

    this.handleScroll();
    if (this.invertedHeader && window.scrollY < 28) {
      this.applyInvertedHeaderStyle();
    } else {
      this.applyNormalizedHeaderStyle();
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleHeaderClick);
    document.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("scroll", this.handleScroll);

    window.removeEventListener("resize", this.handleViewportResize);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener(
        "resize",
        this.handleViewportResize
      );
      window.visualViewport.removeEventListener(
        "scroll",
        this.handleViewportResize
      );
    }

    this.unlockScroll();
    this.unlockPageScroll();
  }

  getElements() {
    const root = this;

    const searchLayer = root.querySelector("[data-header-search]");
    const searchInput = searchLayer
      ? searchLayer.querySelector("[data-header-search-input]")
      : null;

    return {
      header: root.querySelector(".site-header"),
      headerContent: root.querySelector("[data-header-content]"),

      mobileMenu: root.querySelector("[data-mobile-menu]"),
      mobileToggles: root.querySelectorAll("[data-mobile-menu-toggle]"),

      megaRoot: root.querySelector("[data-mega-root]"),
      megaTrigger: root.querySelector("[data-mega-trigger]"),
      megaDropdown: root.querySelector("[data-mega-dropdown]"),

      searchLayer,
      searchOpenButton: root.querySelector("[data-header-search-open]"),
      searchCloseButton: root.querySelector("[data-header-search-close]"),
      searchInput,

      localeToggle: root.querySelector("[data-header-locale-toggle]"),
      localeDropdown: root.querySelector("[data-header-locale-dropdown]"),
      localeForm: root.querySelector("[data-header-locale-dropdown] form"),
      mobileLocaleForm: root.querySelector("#MobileCountryForm"),
      mobileCountrySelect: root.querySelector("[data-mobile-country-select]"),
    };
  }

  initMobileLocaleSelect() {
    const { mobileLocaleForm, mobileCountrySelect } = this.getElements();
    if (!mobileLocaleForm || !mobileCountrySelect) return;

    const trigger = mobileLocaleForm.querySelector(
      "[data-mobile-locale-trigger]"
    );
    if (trigger) {
      trigger.addEventListener("click", (e) => {
        try {
          mobileCountrySelect.focus();
        } catch {}
      });
    }

    mobileCountrySelect.addEventListener("change", (e) => {
      const countryCode = e.target.value;
      if (!countryCode) return;

      this.submitMobileLocaleCountry(countryCode);
    });
  }

  submitMobileLocaleCountry(countryCode) {
    const { mobileLocaleForm } = this.getElements();
    if (!mobileLocaleForm) return;

    const input = mobileLocaleForm.querySelector('input[name="country_code"]');
    if (!input) return;

    input.value = countryCode;

    this.setReturnToForCountry(mobileLocaleForm, countryCode);

    mobileLocaleForm.submit();
  }

  handleScroll() {
    const above = window.scrollY >= 28;
    if (above === this.scrollActive) return;

    this.scrollActive = above;

    if (above) {
      this.setAttribute("data-active", "true");
      this.applyNormalizedHeaderStyle();
      return;
    }

    this.removeAttribute("data-active");

    if (this.invertedHeader && !this.megaMenuOpen && !this.isSearchOpen) {
      this.applyInvertedHeaderStyle();
    }
  }

  isRestrictedPath(pathname) {
    const restricted = ["/collections/merch"];

    return restricted.some((p) => pathname.includes(p));
  }

  ensureReturnToInput(form) {
    if (!form) return null;

    let input = form.querySelector('input[name="return_to"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "return_to";
      form.appendChild(input);
    }
    return input;
  }

  setReturnToForCountry(form, countryCode) {
    if (!form) return;

    const input = this.ensureReturnToInput(form);
    if (!input) return;

    const pathname = window.location.pathname || "/";
    const search = window.location.search || "";
    const current = `${pathname}${search}`;

    const isRestricted = this.isRestrictedPath(pathname);
    const isUS = (countryCode || "").toUpperCase() === "US";

    input.value = isRestricted && !isUS ? "/" : current;
  }

  applyNormalizedHeaderStyle() {
    const { header } = this.getElements();
    if (!header) return;

    header.style.color = "black";
    header.style.background = "white";
    header.style.borderBottomColor = "black";
  }

  applyInvertedHeaderStyle() {
    const { header } = this.getElements();
    if (!header) return;

    header.style.color = "white";
    header.style.background = "transparent";
    header.style.borderBottomColor = "transparent";
  }

  initMegaMenuHoverDelay() {
    const CLOSE_DELAY_MS = 120;
    const { megaRoot, megaTrigger, megaDropdown } = this.getElements();
    if (!megaRoot || !megaTrigger || !megaDropdown) return;

    let closeTimer = null;

    const clearCloseTimer = () => {
      if (!closeTimer) return;
      clearTimeout(closeTimer);
      closeTimer = null;
    };

    const openMegaMenu = () => {
      this.megaMenuOpen = true;
      clearCloseTimer();
      megaRoot.setAttribute("data-open", "true");
      this.applyNormalizedHeaderStyle();
      this.closeSearch();
      this.closeLocaleDropdown();
    };

    const scheduleCloseMegaMenu = () => {
      clearCloseTimer();
      closeTimer = setTimeout(() => {
        this.megaMenuOpen = false;
        megaRoot.removeAttribute("data-open");

        if (this.invertedHeader && window.scrollY < 28 && !this.isSearchOpen) {
          this.applyInvertedHeaderStyle();
        }

        closeTimer = null;
      }, CLOSE_DELAY_MS);
    };

    megaRoot.addEventListener("pointerenter", openMegaMenu);
    megaRoot.addEventListener("pointerleave", scheduleCloseMegaMenu);

    megaTrigger.addEventListener("focusin", openMegaMenu);
    megaTrigger.addEventListener("focusout", scheduleCloseMegaMenu);

    megaDropdown.addEventListener("focusin", openMegaMenu);
    megaDropdown.addEventListener("focusout", scheduleCloseMegaMenu);
  }

  handleHeaderClick(e) {
    if (this.handleMobileMenuToggleClick(e)) return;
    if (this.handleLocaleOptionClick(e)) return;
    if (this.handleSearchOpenClick(e)) return;
    if (this.handleSearchCloseClick(e)) return;

    if (this.handleClickOutsideSearchToClose(e)) return;
  }

  initLocaleHover() {
    const { localeToggle, localeDropdown } = this.getElements();
    if (!localeToggle || !localeDropdown) return;

    const OPEN_DELAY_MS = 0;
    const CLOSE_DELAY_MS = 120;

    let closeTimer = null;
    let openTimer = null;

    const clearTimers = () => {
      if (closeTimer) clearTimeout(closeTimer);
      if (openTimer) clearTimeout(openTimer);
      closeTimer = null;
      openTimer = null;
    };

    const open = () => {
      clearTimers();
      openTimer = setTimeout(() => {
        this.openLocaleDropdown();
      }, OPEN_DELAY_MS);
    };

    const scheduleClose = () => {
      clearTimers();
      closeTimer = setTimeout(() => {
        this.closeLocaleDropdown();
      }, CLOSE_DELAY_MS);
    };

    localeToggle.addEventListener("pointerenter", open);
    localeDropdown.addEventListener("pointerenter", open);

    localeToggle.addEventListener("pointerleave", scheduleClose);
    localeDropdown.addEventListener("pointerleave", scheduleClose);

    localeToggle.addEventListener("focusin", open);
    localeToggle.addEventListener("focusout", scheduleClose);
    localeDropdown.addEventListener("focusin", open);
    localeDropdown.addEventListener("focusout", scheduleClose);
  }

  handleLocaleOptionClick(e) {
    if (!this.isLocaleOpen) return false;

    const option = e.target.closest("a[data-value]");
    if (!option) return false;

    const { localeDropdown } = this.getElements();
    if (!localeDropdown || !localeDropdown.contains(option)) return false;

    e.preventDefault();

    const countryCode = option.dataset.value;
    if (!countryCode) return true;

    this.submitLocaleCountry(countryCode);
    return true;
  }

  openLocaleDropdown() {
    const { localeDropdown, localeToggle, megaRoot } = this.getElements();
    if (!localeDropdown) return;

    this.isLocaleOpen = true;

    if (megaRoot) megaRoot.removeAttribute("data-open");
    this.megaMenuOpen = false;

    this.closeSearch();

    if (this._localeHideTimer) {
      clearTimeout(this._localeHideTimer);
      this._localeHideTimer = null;
    }
    if (this._localeShowTimer) {
      clearTimeout(this._localeShowTimer);
      this._localeShowTimer = null;
    }

    localeDropdown.hidden = false;
    localeDropdown.removeAttribute("data-open");

    if (localeToggle) localeToggle.setAttribute("aria-expanded", "true");

    this.applyNormalizedHeaderStyle();

    this._localeShowTimer = window.setTimeout(() => {
      if (!this.isLocaleOpen) return;
      localeDropdown.setAttribute("data-open", "true");
      this._localeShowTimer = null;
    }, 10);
  }

  closeLocaleDropdown() {
    const { localeDropdown, localeToggle } = this.getElements();
    if (!localeDropdown) return;

    this.isLocaleOpen = false;

    if (this._localeShowTimer) {
      clearTimeout(this._localeShowTimer);
      this._localeShowTimer = null;
    }
    if (this._localeHideTimer) {
      clearTimeout(this._localeHideTimer);
      this._localeHideTimer = null;
    }

    localeDropdown.removeAttribute("data-open");
    if (localeToggle) localeToggle.setAttribute("aria-expanded", "false");

    this._localeHideTimer = window.setTimeout(() => {
      if (this.isLocaleOpen) return;
      localeDropdown.hidden = true;
      this._localeHideTimer = null;
    }, 300);

    if (
      this.invertedHeader &&
      window.scrollY < 28 &&
      !this.megaMenuOpen &&
      !this.isSearchOpen
    ) {
      this.applyInvertedHeaderStyle();
    }
  }

  submitLocaleCountry(countryCode) {
    const { localeForm, localeDropdown, localeToggle } = this.getElements();
    if (!localeForm) return;

    const input = localeForm.querySelector('input[name="country_code"]');
    if (!input) return;

    input.value = countryCode;

    this.setReturnToForCountry(localeForm, countryCode);

    localeForm.submit();

    this.isLocaleOpen = false;

    if (localeToggle) localeToggle.setAttribute("aria-expanded", "false");

    if (localeDropdown) {
      localeDropdown.removeAttribute("data-open");

      if (this._localeHideTimer) clearTimeout(this._localeHideTimer);
      this._localeHideTimer = window.setTimeout(() => {
        if (this.isLocaleOpen) return;
        localeDropdown.hidden = true;
        this._localeHideTimer = null;
      }, 300);
    }
  }

  handleMobileMenuToggleClick(e) {
    const btn = e.target.closest("[data-mobile-menu-toggle]");
    if (!btn) return false;

    e.preventDefault();
    this.toggleMobileMenu();
    return true;
  }

  handleSearchOpenClick(e) {
    const btn = e.target.closest("[data-header-search-open]");
    if (!btn) return false;

    e.preventDefault();
    this.toggleSearch();
    return true;
  }

  handleSearchCloseClick(e) {
    const btn = e.target.closest("[data-header-search-close]");
    if (!btn) return false;

    e.preventDefault();
    this.closeSearch();
    return true;
  }

  handleClickOutsideSearchToClose(e) {
    if (!this.isSearchOpen) return false;

    const { searchLayer, searchOpenButton } = this.getElements();
    if (!searchLayer) return false;

    const clickedInsideSearch = searchLayer.contains(e.target);
    const clickedSearchTrigger =
      searchOpenButton && searchOpenButton.contains(e.target);

    if (clickedInsideSearch || clickedSearchTrigger) return false;

    this.closeSearch();
    return true;
  }

  handleKeyDown(e) {
    if (e.key !== "Escape") return;

    if (this.isSearchOpen) this.closeSearch();
    if (this.isLocaleOpen) this.closeLocaleDropdown();
    if (this.isMobileMenuOpen) this.closeMobileMenu();
  }

  lockPageScroll() {
    this._scrollLockActive = true;
    document.body.style.overflow = "hidden";
  }

  unlockPageScroll() {
    this._scrollLockActive = false;
    document.body.style.overflow = "auto";
  }

  toggleMobileMenu() {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  setViewportUnit() {
    const vv = window.visualViewport;
    const h = vv && vv.height ? vv.height : window.innerHeight;

    const vvh = h * 0.01;
    document.documentElement.style.setProperty("--vvh", `${vvh}px`);
  }

  handleViewportResize() {
    this.setViewportUnit();

    if (this.isMobileMenuOpen) {
      this.applyMobileMenuAnnouncementOffset();
    }
  }

  applyMobileMenuAnnouncementOffset() {
    const { mobileMenu } = this.getElements();
    if (!mobileMenu) return;

    const ANNOUNCEMENT_HEIGHT = 28;
    const scrolled = Math.max(
      0,
      Math.min(ANNOUNCEMENT_HEIGHT, window.scrollY || 0)
    );
    const remaining = ANNOUNCEMENT_HEIGHT - scrolled;

    mobileMenu.style.height = `calc((var(--vvh, 1vh) * 100) - 56px - ${remaining}px)`;
  }

  openMobileMenu() {
    this.applyNormalizedHeaderStyle();

    const { mobileMenu, mobileToggles } = this.getElements();
    if (!mobileMenu || document.readyState !== "complete") return;

    this.isMobileMenuOpen = true;

    this.setViewportUnit();
    this.applyMobileMenuAnnouncementOffset();

    this.lockPageScroll();

    mobileMenu.setAttribute("data-open", "true");
    mobileToggles.forEach((btn) => {
      btn.setAttribute("data-open", "true");
      btn.setAttribute("aria-expanded", "true");
    });
  }

  closeMobileMenu() {
    if (
      this.invertedHeader &&
      window.scrollY < 28 &&
      !this.megaMenuOpen &&
      !this.isSearchOpen
    ) {
      this.applyInvertedHeaderStyle();
    }

    const { mobileMenu, mobileToggles } = this.getElements();
    if (!mobileMenu) return;

    this.isMobileMenuOpen = false;

    mobileMenu.removeAttribute("data-open");
    mobileToggles.forEach((btn) => {
      btn.removeAttribute("data-open");
      btn.setAttribute("aria-expanded", "false");
    });

    mobileMenu.style.height = "";

    this.unlockPageScroll();
  }

  toggleSearch() {
    if (this.isSearchOpen) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    const { searchLayer, megaRoot, searchInput } = this.getElements();
    if (!searchLayer) return;

    this.isSearchOpen = true;

    if (megaRoot) megaRoot.removeAttribute("data-open");
    this.megaMenuOpen = false;

    this.closeLocaleDropdown();

    searchLayer.classList.remove("hidden");
    searchLayer.style.opacity = "1";
    searchLayer.style.pointerEvents = "auto";

    this.applyNormalizedHeaderStyle();

    if (searchInput) {
      requestAnimationFrame(() => {
        try {
          searchInput.focus();
        } catch {}
      });
    }
  }

  closeSearch() {
    const { searchLayer } = this.getElements();
    if (!searchLayer) return;

    this.isSearchOpen = false;

    searchLayer.style.opacity = "0";
    searchLayer.style.pointerEvents = "none";

    window.setTimeout(() => {
      if (this.isSearchOpen) return;
      searchLayer.classList.add("hidden");
    }, 300);

    if (this.invertedHeader && window.scrollY < 28 && !this.megaMenuOpen) {
      this.applyInvertedHeaderStyle();
    }
  }
}

customElements.define("site-header", SiteHeader);

/*
Site Flyout
*/

if (!customElements.get("site-flyout")) {
  class SiteFlyout extends HTMLElement {
    connectedCallback() {
      this.activePanels = [];
      this.setupKeyboardListeners();
    }

    setupKeyboardListeners() {
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.activePanels.length > 0) {
          const lastPanel = this.activePanels[this.activePanels.length - 1];
          this.closePanel(lastPanel);
        }
      });
    }

    openPanel(panel = "cart") {
      const panelEl = this.findPanel(panel);

      if (panelEl) {
        panelEl.classList.add("active");
        if (!this.activePanels.includes(panel)) {
          this.activePanels.push(panel);
        }
        this.handleOpenOrClose();

        this.manageFocusAfterPanelOpen(panel, panelEl);
      }
    }

    manageFocusAfterPanelOpen(panelName, panelElement) {
      if (panelName === "cart") {
        setTimeout(() => {
          const siteCart = document.querySelector("site-cart");
          if (siteCart) {
            if (!siteCart.hasAttribute("tabindex")) {
              siteCart.setAttribute("tabindex", "-1");
            }
            if (!siteCart.hasAttribute("role")) {
              siteCart.setAttribute("role", "region");
            }
            if (!siteCart.hasAttribute("aria-label")) {
              siteCart.setAttribute("aria-label", "Shopping cart");
            }

            siteCart.focus();
          } else {
            const closeButton =
              panelElement.querySelector("close-panel button");
            if (closeButton) {
              closeButton.focus();
            }
          }
        }, 100);
      } else {
        const closeButton = panelElement.querySelector("close-panel button");
        if (closeButton) {
          closeButton.focus();
        } else {
          const focusableElement = panelElement.querySelector(
            "button, [tabindex], input, select, textarea, a[href]"
          );
          if (focusableElement) {
            focusableElement.focus();
          }
        }
      }
    }

    closePanel(panel) {
      const panelEl = this.findPanel(panel);

      if (panelEl) {
        panelEl.classList.remove("active");
        this.activePanels = this.activePanels.filter((p) => p !== panel);
        this.handleOpenOrClose();

        this.manageFocusAfterPanelClose();
      }
    }

    manageFocusAfterPanelClose() {
      if (this.activePanels.length > 0) {
        const focusTarget = this.getCurrentFocusTarget();

        if (focusTarget) {
          if (
            focusTarget.closest("site-cart") &&
            !focusTarget.hasAttribute("tabindex")
          ) {
            focusTarget.setAttribute("tabindex", "-1");
          }

          focusTarget.focus();

          const currentPanel = this.activePanels[this.activePanels.length - 1];
        }
      }
    }

    getCurrentFocusTarget() {
      if (this.activePanels.length === 0) {
        return null;
      }

      const currentPanel = this.activePanels[this.activePanels.length - 1];
      const panelElement = this.findPanel(currentPanel);

      if (panelElement) {
        if (currentPanel === "cart") {
          const siteCart = document.querySelector("site-cart");
          if (siteCart) {
            if (!siteCart.hasAttribute("tabindex")) {
              siteCart.setAttribute("tabindex", "-1");
            }
            if (!siteCart.hasAttribute("role")) {
              siteCart.setAttribute("role", "region");
            }
            if (!siteCart.hasAttribute("aria-label")) {
              siteCart.setAttribute("aria-label", "Shopping cart");
            }
            return siteCart;
          }
        }

        const closeButton = panelElement.querySelector("close-panel button");
        if (closeButton) {
          return closeButton;
        }

        const panelTrigger = document.querySelector(
          `[data-panel="${currentPanel}"]`
        );
        if (panelTrigger) {
          return panelTrigger;
        }

        const focusableElement = panelElement.querySelector(
          "button, [tabindex], input, select, textarea, a[href]"
        );
        if (focusableElement) {
          return focusableElement;
        }
      }

      return null;
    }

    closeAllPanels() {
      this.activePanels.forEach((panel) => {
        this.closePanel(panel);
      });
    }

    findPanel(panel) {
      return this.querySelector(
        `flyout-panel[data-name="${panel}"] .flyout-panel`
      );
    }

    handleOpenOrClose() {
      const veil = this.querySelector(".flyout-veil");

      if (this.activePanels.length > 0) {
        document.body.style.overflow = "hidden";
        veil.classList.add("active");
      } else {
        document.body.removeAttribute("style");
        veil.classList.remove("active");
      }
    }
  }

  customElements.define("site-flyout", SiteFlyout);
}

/*
Open Panel
*/

class OpenPanel extends HTMLElement {
  constructor() {
    super();
    this.panel = this.dataset.panel || "cart";
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.error("ToggleFlyout needs a child button");
    }

    this.addEventListener("click", (e) => {
      const flyout = document.querySelector("site-flyout");

      if (flyout) {
        flyout.openPanel(this.panel);
      }
    });
  }
}

customElements.define("open-panel", OpenPanel);

/*
Close Panel
*/

class ClosePanel extends HTMLElement {
  constructor() {
    super();
    this.panel = this.dataset.panel || "cart";
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.error("ToggleFlyout needs a child button");
    }

    this.addEventListener("click", (e) => {
      const flyout = document.querySelector("site-flyout");

      if (flyout) {
        flyout.closePanel(this.panel);
      }
    });
  }
}

customElements.define("close-panel", ClosePanel);

/*
Close Panels
*/

class ClosePanels extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.error("ClosePanels needs a child button");
    }

    this.addEventListener("click", (e) => {
      const flyout = document.querySelector("site-flyout");
      flyout.closeAllPanels();
    });
  }
}

customElements.define("close-panels", ClosePanels);

/*
Carousel Prev
*/

class CarouselPrev extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to previous!");
    }
    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");

      if (carousel) {
        carousel.previous();
      } else {
        console.error("<carousel-prev> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-prev", CarouselPrev);

/*
Carousel Next
*/

class CarouselNext extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to next!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");
      if (carousel) {
        carousel.next();
      } else {
        console.error("<carousel-next> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-next", CarouselNext);

/*
Carousel Pager
*/

class CarouselPager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const pagerButtons = Array.from(this.querySelectorAll("button"));

    if (!pagerButtons.length > 1) {
      console.warn("No need for a pager with only one item!");
    } else {
      pagerButtons[0].classList.add("active");
    }

    pagerButtons.forEach((pager, i) => {
      pager.addEventListener("click", (e) => {
        const carousel = e.target.closest("site-carousel");

        if (carousel) {
          carousel.slideTo(i);
        } else {
          console.error(
            "<carousel-pager> needs to be a child of <site-carousel>"
          );
        }
      });
    });
  }
}

customElements.define("carousel-pager", CarouselPager);

/*
 * Site Carousel
 */

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.config = null;
  }

  connectedCallback() {
    this.config = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 1, spaceBetween: 20, loop: false };

    const prevSelector = this.dataset.prevSelector || ".carousel-prev";
    const nextSelector = this.dataset.nextSelector || ".carousel-next";

    const prevEl = this.querySelector(prevSelector);
    const nextEl = this.querySelector(nextSelector);

    const scrollbarEl = this.querySelector(".carousel-scrollbar");
    const paginationEl = this.querySelector(".carousel-pager");

    const updatePaginationTheme = (swiper) => {
      const activeSlide = swiper?.slides?.[swiper.activeIndex];
      const shouldInvert = activeSlide?.dataset?.invertPagination === "true";
      this.classList.toggle("invert-pagination", shouldInvert);
    };

    const swiperOptions = {
      ...this.config,
      on: {
        init: (swiper) => {
          this.classList.remove("opacity-0");
          updatePaginationTheme(swiper);
        },
        slideChange: (swiper) => updatePaginationTheme(swiper),
      },
    };

    if (prevEl || nextEl) {
      swiperOptions.navigation = { nextEl, prevEl };
    }

    if (scrollbarEl) {
      swiperOptions.scrollbar = { el: scrollbarEl, draggable: false };
    }

    this.isScrollbar = this.dataset.isScrollbar == "true";

    if (paginationEl && !this.isScrollbar) {
      swiperOptions.pagination = {
        el: paginationEl,
        bulletClass: "carousel-bullet",
        bulletActiveClass: "active",
        clickable: true,
      };
    }

    if (scrollbarEl && this.isScrollbar) {
      swiperOptions.scrollbar = {
        el: scrollbarEl,
      };
    }

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, swiperOptions);
  }

  previous() {
    if (this.swiper) this.swiper.slidePrev();
  }

  next() {
    if (this.swiper) this.swiper.slideNext();
  }

  slideTo(i) {
    if (this.swiper) this.swiper.slideTo(i);
  }
}

customElements.define("site-carousel", SiteCarousel);

/*
Cart Item
*/

class CartItem extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.idx = this.dataset.idx;

    this.selectedVariant = JSON.parse(this.dataset.selectedVariant);

    this.removeEl = this.querySelector(".remove");
    this.quantityInputEl = this.querySelector("number-input input");
    this.quantityControlEl = this.querySelector("number-input");
    this.upgradeButtonEl = this.querySelector(".upgrade-to-sub-js");
    this.sellingPlanSelectEl = this.querySelector(".selling-plan-select");
    this.totals = this.querySelector(".cart-item__totals");
    this.siteCart = this.closest("site-cart");

    this.addRemoveEvent();
    this.addQuantityChangeEvent();
    this.addSubscriptionHandlers();
  }

  showLoader() {
    this.totals.firstElementChild.classList.add("opacity-0");
    this.totals.lastElementChild.classList.remove("hidden");
  }

  hideLoader() {
    this.totals.firstElementChild.classList.remove("opacity-0");
    this.totals.lastElementChild.classList.add("hidden");
  }

  removeItem(idx = this.idx) {
    this.showLoader();
    this.siteCart.updateQuantity(idx, 0);
  }

  addRemoveEvent() {
    if (this.removeEl) {
      this.removeEl.addEventListener("click", () => {
        this.removeItem();
      });
    }
  }

  addQuantityChangeEvent() {
    if (!this.quantityInputEl) return;

    this.quantityInputEl.addEventListener("change", () => {
      this.showLoader();
      this.siteCart.updateQuantity(this.idx, +this.quantityInputEl.value);
    });
  }

  addSubscriptionHandlers() {
    if (this.upgradeButtonEl && this.sellingPlanSelectEl) {
      this.sellingPlanSelectEl.addEventListener("change", async () => {
        const planId = this.sellingPlanSelectEl.value;
        if (!planId) return;

        const qty = this.quantityInputEl
          ? parseInt(this.quantityInputEl.value, 10)
          : 1;
        await this.convertLineToSubscription({ planId, quantity: qty });
      });
    }
  }

  async convertLineToSubscription({ planId, quantity }) {
    this.showLoader();

    try {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ line: this.idx, quantity: 0 }),
      });

      await this.siteCart.addVariantToCart(
        this.selectedVariant.id,
        quantity,
        parseInt(planId, 10)
      );

      this.siteCart.handleRewards();
    } catch (err) {
      console.error("Subscription switch failed:", err);
    } finally {
      this.hideLoader();
    }
  }
}

customElements.define("cart-item", CartItem);

/*
Site Cart
*/

const formatMoneyNoTrailingZeros = (cents) => {
  const currencySymbol = window?.Bero?.storeCurrencySymbol || "$";
  const v = (Number(cents) || 0) / 100;
  const s = v.toFixed(2);
  const out = s.endsWith(".00") ? s.slice(0, -3) : s;
  return `${currencySymbol}${out}`;
};

const applyPricingState = () => {
  const isMember =
    !!window?.Bero?.hasBeromasterInCart || !!window?.Bero?.isRivoSubscriber;

  const toCents = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  document.querySelectorAll("[data-price-wrap]").forEach((wrap) => {
    const compareEl = wrap.querySelector("[data-price-compare]");
    const priceEl = wrap.querySelector("[data-price-active]");
    if (!priceEl) return;

    const hasShopifyLinePricing =
      wrap.dataset.shopifyFinalLine !== undefined ||
      wrap.dataset.shopifyOriginalLine !== undefined;

    if (hasShopifyLinePricing) {
      const originalLine = toCents(wrap.dataset.shopifyOriginalLine);
      const finalLine = toCents(wrap.dataset.shopifyFinalLine);

      const hasVip = wrap.dataset.hasVip === "true";
      const vipLine = toCents(wrap.dataset.vipLine);

      const activeCents = hasVip ? vipLine : finalLine;

      let compareCents = 0;
      if (hasVip) {
        compareCents = originalLine > 0 ? originalLine : finalLine;
      } else if (originalLine > finalLine) {
        compareCents = originalLine;
      }

      priceEl.textContent = formatMoneyNoTrailingZeros(activeCents);

      if (compareEl) {
        if (compareCents > activeCents) {
          compareEl.classList.remove("hidden");
          compareEl.textContent = formatMoneyNoTrailingZeros(compareCents);
        } else {
          compareEl.classList.add("hidden");
          compareEl.textContent = "";
        }
      }

      return;
    }

    const isBeromasterProduct =
      priceEl.dataset.isBeromaster === "true" ||
      compareEl?.dataset.isBeromaster === "true";

    const useMember = isMember || isBeromasterProduct;

    const nextPriceRaw = useMember
      ? priceEl.dataset.priceMember
      : priceEl.dataset.priceNonmember;

    if (
      nextPriceRaw === undefined ||
      nextPriceRaw === null ||
      nextPriceRaw === ""
    ) {
      return;
    }

    const nextPrice = toCents(nextPriceRaw);
    priceEl.textContent = formatMoneyNoTrailingZeros(nextPrice);

    if (!compareEl) return;

    const nextCompareRaw = useMember
      ? compareEl.dataset.compareMember
      : compareEl.dataset.compareNonmember;

    if (
      nextCompareRaw === undefined ||
      nextCompareRaw === null ||
      nextCompareRaw === ""
    ) {
      compareEl.classList.add("hidden");
      compareEl.textContent = "";
      return;
    }

    const nextCompare = toCents(nextCompareRaw);
    const shouldShowCompare = useMember
      ? nextCompare > nextPrice
      : nextCompare > 0;

    if (shouldShowCompare) {
      compareEl.classList.remove("hidden");
      compareEl.textContent = formatMoneyNoTrailingZeros(nextCompare);
    } else {
      compareEl.classList.add("hidden");
      compareEl.textContent = "";
    }
  });
};

class SiteCart extends HTMLElement {
  constructor() {
    super();
  }

  // 1) In connectedCallback: do NOT call reloadCart on cart page init
  connectedCallback() {
    this.flyout = document.querySelector("site-flyout");
    this.gwpVariantId = this.dataset.gwpVariantId;

    this._hasHydrated = false;
    this._isCartPage =
      window.location.pathname === "/cart" ||
      document.body.classList.contains("template-cart") ||
      document.documentElement.classList.contains("template-cart");

    Promise.all([
      this.validateBeromaster(),
      this._isCartPage ? this.fetchCart() : this.reloadCart({ reason: "init" }),
    ]).then(async () => {
      this.handleRewards({ reason: "init" });

      this.addEventListener("click", (e) => {
        const btn = e.target.closest("button");
        if (btn && btn.matches(".remove-bundle")) {
          this.removeBYOB();
        }
      });

      this.currencySymbol = window?.Bero?.storeCurrencySymbol || "$";

      await this.loadCart();

      this._hasHydrated = true;
    });
  }

  async openCart() {
    this.reloadCart();
    this.handleRewards();
    this.flyout.openPanel("cart");
  }

  async validateBeromaster() {
    if (window.Bero.isRivoSubscriber && window.Bero.hasBeromasterInCart) {
      await this.removeItem(window.Bero.beromasterVariantId, true);
    }
  }

  getCartSections() {
    return [
      { section: "main-cart-items", selector: "#main-cart-items" },
      { section: "main-cart-footer", selector: "#main-cart-footer" },
      { section: "main-cart-header", selector: "#main-cart-header" },
    ];
  }

  getSectionInnerHTML(html, selector) {
    const el = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    return el ? el.innerHTML : "";
  }

  renderCartSections(sections) {
    const getSections = this.getCartSections();
    const sectionsArr = Object.keys(sections);

    sectionsArr.forEach((sectionKey) => {
      const activeSection = getSections.find((s) => s.section === sectionKey);
      if (!activeSection) return;

      const selectors = activeSection.selectors ?? [activeSection.selector];

      selectors.forEach((selector) => {
        const el = document.querySelector(selector);
        if (!el) return;

        const html = this.getSectionInnerHTML(sections[sectionKey], selector);

        if (html) el.innerHTML = html;
      });
    });
  }

  buildConfig(body) {
    return {
      body,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };
  }

  async addBYOB(variantIds, _dynamicBoxId, _dynamicBoxIndex, selling_plan) {
    await this.removeBYOB(false);

    const items = variantIds.map((id) => ({
      id,
      quantity: 1,
      properties: { _dynamicBoxId, _dynamicBoxIndex },
      ...(selling_plan ? { selling_plan } : {}),
    }));

    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);
    this.flyout.openPanel("cart");

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }

        this.loadCart();
        this.handleRewards();
      })
      .catch(() => {});
  }

  async removeItem(variantId, shouldReload = false) {
    const updates = { [variantId]: 0 };
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates, sections });
    const config = this.buildConfig(body);

    await fetch("/cart/update", config)
      .then((res) => res.json())
      .then(() => this.reloadCart({ reason: "mutation" }));

    if (shouldReload) this.handleRewards({ reason: "mutation" });
    await this._addMatchingGwpGifts();
  }

  async removeBYOB(shouldRefresh = true) {
    const updates = {};

    this.cartData.items.forEach((item) => {
      if (item.properties && item.properties._dynamicBoxId) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length > 0) {
      const sections = this.getCartSections().map((s) => s.section);
      const body = JSON.stringify({ updates, sections });
      const config = this.buildConfig(body);

      await fetch("/cart/update", config).then((res) => res.json());

      if (shouldRefresh) {
        this.reloadCart({ reason: "mutation" });
        this.handleRewards({ reason: "mutation" });
      }
    }
  }

  async refreshBeromasterDiscount() {
    const beromasterId = window.Bero.beromasterVariantId;
    const hasBeromaster = this.cartData.items.some(
      (item) => +item.variant_id === +beromasterId
    );
    window.Bero.hasBeromasterInCart = hasBeromaster;
  }

  async addVariantToCart(
    id,
    quantity = 1,
    sellingPlan,
    properties,
    options = {}
  ) {
    await this.refreshBeromasterDiscount();

    const items = [
      sellingPlan
        ? { id, quantity, selling_plan: sellingPlan, properties }
        : { id, quantity, properties },
    ];

    const { openCart = true } = options || {};

    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    if (openCart && this.flyout) this.flyout.openPanel("cart");

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then(async (response) => {
        const productDetailsController = document.querySelector("main-product");
        if (productDetailsController)
          productDetailsController.updateProductDetailsHTML();

        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }

        await this.reloadCart({ reason: "mutation" });
        await this.handleRewards({ reason: "mutation" });
        await this._addMatchingGwpGifts();
      })
      .catch((err) => console.log(err.message));
  }

  async _addMatchingGwpGifts() {
    if (this._gwpProcessing) return;
    this._gwpProcessing = true;

    try {
      const gwpDiscounts = window.Bero?.gwpDiscounts;
      if (!Array.isArray(gwpDiscounts) || !gwpDiscounts.length) return;

      const automaticDiscounts = gwpDiscounts.filter(
        (d) => d.discount_type === "Automatic GWP Discount"
      );
      if (!automaticDiscounts.length) return;

      // Customer state
      const customerIsLoggedIn = window.Bero?.customerIsLoggedIn ?? false;
      const customerOrdersCount = window.Bero?.customerOrdersCount ?? 0;
      const currentMarketId = window.Bero?.currentMarketId ?? "";

      // Normalized customer tags
      const rawTags = window.Bero?.customerTags;
      const normalizedCustomerTags = Array.isArray(rawTags)
        ? rawTags.map((t) => t.trim().toLowerCase())
        : [];

      // Cart state — total_price is in cents; threshold in metafield is in dollars
      const cartSubtotalDollars = (this.cartData?.total_price ?? 0) / 100;
      const cartItems = this.cartData?.items ?? [];

      // --- Cart query helpers ---

      const resolveVariantIds = (products) =>
        (products ?? [])
          .flatMap((p) => p.variants ?? [])
          .map((v) => {
            const gid = String(v.id ?? "");
            return gid.includes("/") ? gid.split("/").pop() : gid;
          })
          .filter(Boolean);

      const isVariantInCart = (variantId) =>
        cartItems.some((item) => String(item.variant_id) === variantId);

      const hasAnyVariantInCart = (variantIds, minQty = 1) =>
        variantIds.some((id) => {
          const line = cartItems.find((item) => String(item.variant_id) === id);
          return line && line.quantity >= minQty;
        });

      const hasAllVariantsInCart = (variantIds, minQty = 1) =>
        variantIds.every((id) => {
          const line = cartItems.find((item) => String(item.variant_id) === id);
          return line && line.quantity >= minQty;
        });

      const hasNonExcludedItemInCart = (excludedVariantIds) =>
        cartItems.some((item) => !excludedVariantIds.includes(String(item.variant_id)));

      // --- Condition checks (mirrors checkout extension pipeline) ---

      const passesThresholdCheck = (threshold) => {
        if (threshold === undefined || threshold === null) return true;
        const t = Number(threshold);
        return (cartSubtotalDollars > 0 && cartSubtotalDollars >= t) ||
               (cartSubtotalDollars <= 0 && t <= 0);
      };

      const passesRequiredProductsCheck = (value) => {
        if (!value.requiredProducts) return false;
        const requiredVariantIds = resolveVariantIds(value.requiredProducts);
        if (requiredVariantIds.length === 0) return true;
        const qty = Number(value.reqProductsQuantity) || 1;
        return value.allProductsRequiredCheck
          ? hasAllVariantsInCart(requiredVariantIds, qty)
          : hasAnyVariantInCart(requiredVariantIds, qty);
      };

      const passesExcludedProductsCheck = (value) => {
        if (!value.excludedProducts) return true;
        const excludedVariantIds = resolveVariantIds(value.excludedProducts);
        if (excludedVariantIds.length === 0) return true;
        if (value.onlyProductsExcludedCheck) {
          return hasNonExcludedItemInCart(excludedVariantIds);
        }
        return !hasAnyVariantInCart(excludedVariantIds);
      };

      const passesAllConditions = (discount) => {
        const { value } = discount;

        if (value.registeredCheck && !customerIsLoggedIn) return false;

        if (value.firstOrder && customerOrdersCount !== value.firstOrderValue) return false;

        let requiredTags = value.tags ?? [];
        if (typeof requiredTags === "string") requiredTags = requiredTags ? [requiredTags] : [];
        requiredTags = requiredTags.map((t) => t.trim().toLowerCase()).filter(Boolean);
        if (requiredTags.length > 0 && !requiredTags.some((tag) => normalizedCustomerTags.includes(tag))) return false;

        if (!passesThresholdCheck(value.threshold)) return false;

        if (!value.markets.includes(currentMarketId)) return false;

        if (!passesRequiredProductsCheck(value)) return false;

        if (!passesExcludedProductsCheck(value)) return false;

        return true;
      };

      // --- Collect gifts to add / remove (matching checkout extension pattern) ---

      const allGiftIds = new Set();
      const giftsToAdd = new Set();

      for (const discount of automaticDiscounts) {
        const giftVariantIds = resolveVariantIds(discount.value?.gift);
        if (!giftVariantIds.length) continue;

        for (const id of giftVariantIds) allGiftIds.add(id);

        if (!passesAllConditions(discount)) continue;

        // Edge case guard: skip if required products IS the gift
        if (JSON.stringify(discount.value.requiredProducts) === JSON.stringify(discount.value.gift)) continue;

        for (const id of giftVariantIds) giftsToAdd.add(id);
      }

      const toAdd = [...giftsToAdd].filter((id) => !isVariantInCart(id));
      const toRemove = [...allGiftIds].filter((id) => !giftsToAdd.has(id) && isVariantInCart(id));

      if (toAdd.length === 0 && toRemove.length === 0) return;

      // Remove ineligible gifts first (single cart/update call)
      if (toRemove.length > 0) {
        const updates = Object.fromEntries(toRemove.map((id) => [id, 0]));
        await fetch("/cart/update", this.buildConfig(JSON.stringify({ updates })))
          .then((r) => r.json())
          .catch((err) => console.log("GWP gift remove error:", err.message));
      }

      // Add eligible gifts
      for (const variantId of toAdd) {
        const code = automaticDiscounts.find((d) =>
          resolveVariantIds(d.value?.gift).includes(variantId)
        )?.code;
        await fetch(
          "/cart/add",
          this.buildConfig(
            JSON.stringify({
              items: [{ id: variantId, quantity: 1, properties: { _gwp: code } }],
            })
          )
        )
          .then((r) => r.json())
          .catch((err) => console.log("GWP gift add error:", err.message));
      }

      await this.reloadCart({ reason: "mutation" });
      await this.handleRewards({ reason: "mutation" });
    } finally {
      this._gwpProcessing = false;
    }
  }

  async fetchSections(sectionIds, sectionsUrl = "/cart") {
    if (!sectionIds.length) return {};
    const qs = new URLSearchParams();
    qs.set("sections", sectionIds.join(","));
    qs.set("sections_url", sectionsUrl);
    const res = await fetch(`/?${qs.toString()}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Section fetch failed: ${res.status} ${res.statusText} ${text}`
      );
    }
    return res.json();
  }

  async reloadCart({ reason = "mutation" } = {}) {
    await this.fetchCart();
    await this.refreshBeromasterDiscount();

    if (this._hasHydrated && reason === "mutation") {
      const productDetailsController = document.querySelector("main-product");
      if (productDetailsController) {
        productDetailsController.updateProductDetailsHTML();
      }

      const quickAddBeromaster = document.querySelector("quick-add-beromaster");
      if (quickAddBeromaster) quickAddBeromaster.updateTextContent();
    }

    const shouldRenderSections = !(this._isCartPage && reason === "init");

    if (shouldRenderSections) {
      const sections = this.getCartSections().map((s) => s.section);
      const response = await this.fetchSections(sections);
      this.renderCartSections(response);
    }

    this.updateCartCount();

    if (!(this._isCartPage && reason === "init")) {
      applyPricingState();
    }
  }

  updateCartCount() {
    const count = Number(this.cartData?.item_count ?? 0);
    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = String(count);
    });
  }

  async updateQuantity(line, quantity) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ line, quantity, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/change", config)
      .then((response) => response.json())
      .then(async () => {
        await this.reloadCart({ reason: "mutation" });
        await this.handleRewards({ reason: "mutation" });
        await this._addMatchingGwpGifts();
      })
      .catch((err) => console.log(err.message));
  }

  async updateCart(updates, shouldRefresh = true) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/update", config)
      .then((response) => response.json())
      .then(async (response) => {
        await this.reloadCart({ reason: "mutation" });
        if (shouldRefresh) this.renderCartSections(response.sections);
      })
      .catch((err) => console.log(err.message));
  }

  async clearCart(shouldRefresh = true) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates: {}, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/clear", config)
      .then((response) => response.json())
      .then(async (response) => {
        await this.reloadCart({ reason: "mutation" });
        if (shouldRefresh) this.renderCartSections(response.sections);
      })
      .catch(() => {});
  }

  async fetchCart() {
    const uri = window.Shopify.routes.root + "cart.js";
    const resp = await fetch(uri, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    this.cartData = await resp.json();
    return this.cartData;
  }

  async handleRewards({ reason = "mutation" } = {}) {
    const rewards = this.querySelector("cart-rewards");
    if (!rewards) return;

    const detect = () => {
      const ds = rewards.dataset || {};
      const country = (
        ds.country ||
        (window.Shopify && Shopify.country) ||
        ""
      ).toUpperCase();
      const currency =
        ds.currency ||
        (window.Shopify && Shopify.currency && Shopify.currency.active) ||
        (country === "GB" ? "GBP" : "USD");
      const locale =
        ds.locale ||
        (document.documentElement.lang || "").replace("_", "-") ||
        (currency === "GBP" ? "en-GB" : "en-US");
      return { currency, locale };
    };

    const { currency, locale } = detect();

    const money = (cents) =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
        .format((Number(cents) || 0) / 100)
        .replace(/\.00$/, "");

    const hideAll = (nodes) => nodes.forEach((n) => n?.classList.add("hidden"));
    const show = (node) => node?.classList.remove("hidden");

    const els = {
      empty: rewards.querySelector('[data-status="empty"]'),
      pending: rewards.querySelector('[data-status="pending"]'),
      earned: rewards.querySelector('[data-status="earned"]'),
      shippingRemaining: rewards.querySelector(".shipping-remaining"),
      bar: rewards.querySelector(".percentage-bar"),
    };

    hideAll([els.empty, els.pending, els.earned]);

    const subtotal = this.cartData?.total_price ?? 0;
    const shippingThreshold = Number(rewards.dataset.shippingThreshold || 0);

    const hasSubscription =
      Array.isArray(this.cartData?.items) &&
      this.cartData.items.some(
        (item) =>
          item?.selling_plan_allocation ||
          item?.selling_plan ||
          item?.selling_plan_id
      );

    const shippingMet =
      hasSubscription ||
      shippingThreshold === 0 ||
      subtotal >= shippingThreshold;

    if (shippingMet) {
      show(els.earned);
      if (els.shippingRemaining) els.shippingRemaining.textContent = "";
    } else if (subtotal === 0 && !hasSubscription) {
      show(els.empty);
      if (els.shippingRemaining) {
        const remaining = Math.max(shippingThreshold - subtotal, 0);
        els.shippingRemaining.textContent = money(remaining);
      }
    } else {
      const remaining = Math.max(shippingThreshold - subtotal, 0);
      if (els.shippingRemaining)
        els.shippingRemaining.textContent = money(remaining);
      show(els.pending);
    }

    const progress = hasSubscription
      ? 100
      : shippingThreshold === 0
      ? 100
      : Math.min((subtotal / Math.max(shippingThreshold, 1)) * 100, 100);

    if (els.bar) els.bar.style.width = `${progress}%`;

    const hasBeromaster =
      Array.isArray(this.cartData?.items) &&
      this.cartData.items.some((item) => item.handle === "beromaster");

    if (hasBeromaster && window?.Bero?.isRivoSubscriber) {
      const beromaster = this.cartData.items.filter(
        (item) => item.handle === "beromaster"
      );
      this.removeItem(`${beromaster[0].variant_id}`, true);
    }

    if (!hasBeromaster && !window?.Bero?.isRivoSubscriber) {
      for (const item of this.cartData.items) {
        if (item.properties?.preOrder) this.removeItem(item.id, true);
      }
    }

    if (!this._hasHydrated || reason === "init") return;

    document
      .querySelectorAll("button[data-add-membership]")
      .forEach((button) => {
        if (hasBeromaster) {
          button.innerHTML = "Already in bag";
          button.disabled = true;
        } else {
          button.innerHTML = `Join today - ${money(5500)}/yr`;
          button.removeAttribute("disabled");
        }
      });
  }

  async removePreOrderWithoutBeromaster(hasBeromaster) {
    if (!hasBeromaster && !window?.Bero?.isRivoSubscriber) {
      for (const item of this.cartData.items) {
        if (item.properties?.preOrder) this.removeItem(item.id, true);
      }
    }
  }

  async removeDiscountIfNotBeomaster(hasBeromaster) {
    if (!hasBeromaster && !window?.Bero?.isRivoSubscriber) {
      await fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ discount: "" }),
      })
        .then((response) => response.json())
        .catch((err) => console.log(err.message));
    }

    if (window?.Bero?.isRivoSubscriber) {
      await fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: {} }),
      })
        .then((response) => response.json())
        .catch((err) => console.log(err.message));
    }
  }

  async removeBeromasterInLogin(hasBeromaster) {
    if (hasBeromaster && window?.Bero?.isRivoSubscriber) {
      const beromaster = cart.items.filter(
        (item) => item.handle === "beromaster"
      );
      this.removeItem(`${beromaster[0].variant_id}`, true);
    }
  }

  async loadCart() {
    if (this._isCartPage) {
      await this.fetchCart();
      await this.refreshBeromasterDiscount();
      this.updateCartCount();
      this.handleRewards({ reason: "init" });
      return;
    }

    await this.reloadCart({ reason: "init" });

    const hasBeromaster =
      Array.isArray(this.cartData?.items) &&
      this.cartData.items.some((item) => item.handle === "beromaster");

    await this.removeBeromasterInLogin(hasBeromaster);
    await this.removePreOrderWithoutBeromaster(hasBeromaster);
    await this.removeDiscountIfNotBeomaster(hasBeromaster);

    await this.reloadCart({ reason: "init" });
  }
}

customElements.define("site-cart", SiteCart);

/*
Number Input
*/

class NumberInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const button = event.target.closest("button");

    if (button) {
      button.name === "increment" ? this.input.stepUp() : this.input.stepDown();

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }
    }
  }
}

customElements.define("number-input", NumberInput);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.becomeMember = this.dataset.becomeMember === "true";

    if (this.becomeMember) {
      this.showLogin();
    } else {
      this.addProductToCart();
    }
  }

  showLogin() {
    const loginButtons = this.querySelectorAll('[name="login"]');
    loginButtons.forEach((button) => {
      const hashUrl = document.createElement("hash-url");
      hashUrl.setAttribute("data-hash", "rivo");

      button.replaceWith(hashUrl);

      hashUrl.appendChild(button);
    });
  }

  addProductToCart() {
    this.addEventListener("click", async (e) => {
      if (
        !e.target.closest('button[type="submit"]') &&
        !e.target.closest("form")
      ) {
        return;
      }

      const form = e.target.closest("form");
      if (!form) return;

      e.preventDefault();
      const $id = form.querySelector('[name="id"]');
      const $qty = this.querySelector('[name="quantity"]');
      const $sellingPlan = form.querySelector('[name="selling_plan"]');
      const preOrderInput = this.querySelector('[name="properties[preorder]"]');
      const preOrderValue = preOrderInput
        ? preOrderInput.value === "true"
        : false;
      const qty = $qty ? $qty.value : 1;
      const sellingPlan =
        $sellingPlan && !!$sellingPlan.value ? $sellingPlan.value.trim() : null;

      const recipientFormV2 = document.querySelector("recipient-form-v2");
      let properties = recipientFormV2
        ? recipientFormV2.propertiesValues()
        : {};
      properties = preOrderValue
        ? { ...properties, preOrder: preOrderValue }
        : properties;

      const giftCardProps = Array.from(
        this.querySelectorAll("[data-gift-card-property]")
      );

      if (giftCardProps.length) {
        giftCardProps.forEach((input) => {
          const propName = input.dataset.giftCardProperty;
          properties = { ...properties, [propName]: input.value };
        });
      }

      const isValidProperties = recipientFormV2
        ? recipientFormV2.validProperties()
        : true;

      const addBeroToggle = document.getElementById("add-beromaster-toggle");
      const addBero = addBeroToggle && addBeroToggle.checked;
      const beroVariantId = addBeroToggle
        ? addBeroToggle.dataset.beromasterVariantId
        : null;

      if ($id && isValidProperties) {
        const cart = document.querySelector("site-cart");
        if (cart) {
          await cart.addVariantToCart($id.value, qty, sellingPlan, properties);

          if (addBero && beroVariantId) {
            const membershipProps = {
              ...(properties || {}),
              beromaster_membership: true,
            };
            await cart.addVariantToCart(
              beroVariantId,
              1,
              null,
              membershipProps
            );
          }
        }
      }
    });
  }
}

customElements.define("cart-add", CartAdd);

/*
Toggle Membership
*/

class ToggleMembership extends HTMLElement {
  connectedCallback() {
    this.siteCart = document.querySelector("site-cart");
    this.ignoreSelector = (this.getAttribute("ignore-selector") || "").trim();

    this.checkboxSelector = (
      this.getAttribute("checkbox-selector") || "input[type='checkbox']"
    ).trim();

    this.triggerSelector = (
      this.getAttribute("trigger-selector") || "[data-add-membership]"
    ).trim();

    this.checkbox = this.querySelector(this.checkboxSelector);
    this.trigger = this.querySelector(this.triggerSelector);

    this.variantInput = this.querySelector("#variant-id");
    this.planInput = this.querySelector("#selling-plan-id");

    this.variantId = this.variantInput
      ? parseInt(this.variantInput.value, 10)
      : null;

    this.planId =
      this.planInput && this.planInput.value
        ? parseInt(this.planInput.value, 10)
        : null;

    this.loader = this.querySelector("[data-toggle-membership-loader]");
    this.priceBlock = this.querySelector("[data-toggle-membership-price]");

    this.isUpdating = false;

    this.onClickCapture = this.onClickCapture.bind(this);
    this.onChange = this.onChange.bind(this);

    this.addEventListener("click", this.onClickCapture, true);

    if (this.checkbox) {
      this.checkbox.addEventListener("change", this.onChange);
    } else {
      this.toggleAttribute("data-checked", false);
    }

    if (!this.checkbox && !this.trigger) {
      console.warn(
        "<toggle-membership>: No checkbox or trigger found. Add a checkbox inside the component or add a button with [data-add-membership], or set trigger-selector/checkbox-selector."
      );
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClickCapture, true);
    if (this.checkbox)
      this.checkbox.removeEventListener("change", this.onChange);
  }

  shouldIgnoreClick(target) {
    if (!this.ignoreSelector) return false;
    return !!target.closest(this.ignoreSelector);
  }

  showLoader() {
    if (this.loader) this.loader.classList.remove("hidden");
  }

  hideLoader() {
    if (this.loader) this.loader.classList.add("hidden");
  }

  hidePrices() {
    if (this.priceBlock) this.priceBlock.classList.add("hidden");
  }

  setChecked(checked) {
    if (this.checkbox) this.checkbox.checked = !!checked;
    this.toggleAttribute("data-checked", !!checked);
  }

  onClickCapture(e) {
    if (this.isUpdating) return;
    if (this.shouldIgnoreClick(e.target)) return;

    const clickedCheckbox =
      this.checkbox &&
      (e.target === this.checkbox || e.target.closest(this.checkboxSelector));

    const clickedTrigger =
      this.trigger &&
      (e.target === this.trigger || e.target.closest(this.triggerSelector));

    if (clickedCheckbox) return;

    if (clickedTrigger) {
      e.preventDefault();
      const next = this.checkbox
        ? this.checkbox.checked
        : this.hasAttribute("data-checked");
      const newVal = !next;
      this.setChecked(newVal);
      this.commit(newVal);
      return;
    }

    if (!this.checkbox) return;

    e.preventDefault();
    const next = !this.checkbox.checked;
    this.setChecked(next);
    this.commit(next);
  }

  onChange(e) {
    if (this.isUpdating) return;
    this.commit(e.currentTarget.checked);
  }

  async commit(checked) {
    if (this.isUpdating) return;

    this.isUpdating = true;

    try {
      if (checked) {
        this.showLoader();
        this.hidePrices();
        const flyout = document.querySelector("site-flyout");
        if (flyout) {
          flyout.closePanel("free-shipping");
          flyout.closePanel("membership");
        }
        await this.addMembership();
      } else {
        this.showLoader();
        await this.removeMembership();
      }
    } catch (err) {
      this.setChecked(!checked);
    } finally {
      this.isUpdating = false;
      this.hideLoader();
    }
  }

  async addMembership() {
    if (!this.siteCart || !this.variantId) return;
    await this.siteCart.addVariantToCart(this.variantId, 1, this.planId, null, {
      openCart: false,
    });
  }

  async removeMembership() {
    if (!this.siteCart) return;

    const membershipItem = this.siteCart.querySelector(
      "cart-item[data-is-membership]"
    );
    if (membershipItem && typeof membershipItem.removeItem === "function") {
      await membershipItem.removeItem();
      return;
    }

    const cart = await this.siteCart.fetchCart();
    const membershipLine = Array.isArray(cart?.items)
      ? cart.items.find((i) => i?.handle === "beromaster")
      : null;

    if (membershipLine && membershipLine.key) {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ updates: { [membershipLine.key]: 0 } }),
      });
      await this.siteCart.reloadCart();
    }
  }
}

customElements.define("toggle-membership", ToggleMembership);

/*
Accordion Item
*/

class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.isAnimating = false;
  }

  connectedCallback() {
    this.startOpen = this.dataset.startOpen === "true";
    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.content = this.querySelector(".content");
    this.arrow = this.querySelector(".accordion-arrow");
    this.plusIcon = this.querySelector(".accordion-plus");

    if (!this.details || !this.content || !this.summary) return;

    this.content.style.overflow = "hidden";
    this.content.style.boxSizing = "border-box";

    if (this.startOpen) {
      this.details.open = true;
      this.content.style.height = "auto";
      this.content.style.opacity = "1";
    } else {
      this.content.style.height = "0";
      this.content.style.opacity = "0";
    }

    this.updateIcon(this.details.open);

    this.summary.addEventListener("click", (e) => {
      e.preventDefault();
      if (!this.isAnimating) this.toggle();
    });
  }

  toggle() {
    this.details.open ? this.close() : this.open();
  }

  open() {
    this.isAnimating = true;
    this.details.open = true;

    this.content.style.height = "0";
    this.content.style.opacity = "0";
    void this.content.offsetHeight;

    const endHeight = this.content.scrollHeight;

    const animation = this.content.animate(
      [
        { height: "0px", opacity: 0 },
        { height: `${endHeight}px`, opacity: 1 },
      ],
      { duration: 300, easing: "ease" }
    );

    this.updateIcon(true);

    this.dispatchEvent(
      new CustomEvent("accordion-item:open", {
        bubbles: true,
        detail: { index: Number(this.dataset.index || 0) },
      })
    );

    animation.onfinish = () => {
      this.content.style.height = "auto";
      this.content.style.opacity = "1";
      this.isAnimating = false;
    };
  }

  close() {
    this.isAnimating = true;

    this.updateIcon(false);

    const startHeight = this.content.scrollHeight;

    const animation = this.content.animate(
      [
        { height: `${startHeight}px`, opacity: 1 },
        { height: "0px", opacity: 0 },
      ],
      { duration: 300, easing: "ease" }
    );

    animation.onfinish = () => {
      this.content.style.height = "0";
      this.content.style.opacity = "0";
      this.details.open = false;
      this.isAnimating = false;
    };
  }

  updateIcon(isOpen) {
    if (this.arrow) {
      this.arrow.style.transform = isOpen ? "rotate(180deg)" : "rotate(0deg)";
    }

    if (this.plusIcon) {
      this.plusIcon.style.transform = isOpen ? "rotate(45deg)" : "rotate(0deg)";
    }
  }
}

customElements.define("accordion-item", AccordionItem);

/*
Hash URL
*/

class HashURL extends HTMLElement {
  connectedCallback() {
    const hash = this.dataset.hash;

    this.querySelector("button").addEventListener("click", () => {
      const baseUrl = window.location.href.split("#")[0];
      window.location.href = `${baseUrl}#${hash}`;
    });
  }
}

customElements.define("hash-url", HashURL);

/*
Currency symbol
*/

const updateCurrencySymbols = () => {
  const currencySymbol = window?.Bero?.storeCurrencySymbol || "$";

  const priceElements = document.querySelectorAll(".dynamic-price-symbol");
  priceElements.forEach((element) => {
    const originalText = element.textContent.trim();
    const updatedText = originalText.replace(/[$£]/g, currencySymbol);
    element.textContent = updatedText;
  });
};

const handleCountryRedirect = () => {
  const blockedUkPaths = ["/collections/bero-collection"];

  const currentCurrency = window?.Bero?.storeCurrencySymbol || "$";
  if (currentCurrency === "£" && blockedUkPaths.includes(location.pathname)) {
    window.location.href = "/";
    return;
  }

  document.addEventListener("click", (e) => {
    const link = e.target.closest(".country-selector__list .disclosure__link");
    if (!link) return;

    if (
      link.dataset.value === "GB" &&
      blockedUkPaths.includes(location.pathname)
    ) {
      e.preventDefault();
      const form = link.closest("form");
      if (!form) return (location.href = "/");

      form.querySelector("input[name='country_code']").value = "GB";
      form.querySelector("input[name='return_to']").value = "/";
      form.submit();
    }
  });
};

document.addEventListener("DOMContentLoaded", () => {
  handleCountryRedirect();
  updateCurrencySymbols();
});

class QuickAddBeromaster extends HTMLElement {
  connectedCallback() {
    this.hasCustomer = this.dataset.customer != "";
    this.button = this.querySelector("button");
    this.addEventListener("click", () => {
      if (this.hasCustomer) {
        window.location.href = "/products/beromaster";
      } else {
        !window?.Bero?.hasBeromasterInCart && this.addMembership();
      }
    });
  }

  async addMembership() {
    const siteCart = document.querySelector("site-cart");
    const sellingPlan = document.querySelector("#selling-plan-id");
    const sellingPlanValue = sellingPlan.value
      ? sellingPlan.value.trim()
      : null;
    const beromasterVariantId = `${window?.Bero?.beromasterVariantId}`;
    try {
      siteCart &&
        (await siteCart.addVariantToCart(
          beromasterVariantId,
          1,
          sellingPlanValue
        ));
      this.updateTextContent();
    } catch (err) {
      console.log(err);
    }
  }

  async updateTextContent() {
    if (this.hasCustomer) {
      this.button.textContent = "Already a member";
    } else {
      if (window?.Bero?.hasBeromasterInCart) {
        this.button.textContent = "Already in bag";
        this.button.disabled = true;
      } else {
        this.button.textContent = "Quick add";
        this.button.disabled = false;
      }
    }
  }
}

customElements.define("quick-add-beromaster", QuickAddBeromaster);

document.addEventListener("DOMContentLoaded", () => {
  const observer = new MutationObserver(() => {
    const button = document.querySelector("#rivo-earning-rule-307688");
    if (button) {
      button.addEventListener("click", () => {
        window.location.href = "https://junip.co/forms/bero";
      });
      observer.disconnect();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
});

/*
Product Item
*/

class ProductItem extends HTMLElement {
  connectedCallback() {
    if (this.dataset.variantInit === "true") return;

    this.card = this.querySelector("[data-product-card]");
    this.picker = this.querySelector("[data-variant-picker]");
    this.idInput = this.querySelector("[data-variant-id-input]");
    this.atcBtn = this.querySelector("[data-atc-btn]");
    this.atcLabel = this.querySelector("[data-atc-label]");
    this.priceEl = this.querySelector("[data-price]");
    this.compareEl = this.querySelector("[data-compare-price]");

    if (!this.picker || !this.idInput) {
      this.dataset.variantInit = "true";
      return;
    }

    this.btns = Array.from(this.picker.querySelectorAll("[data-variant-btn]"));
    if (!this.btns.length) {
      this.dataset.variantInit = "true";
      return;
    }

    this._applyPickerVisibility = () => {
      const isDesktop = window.innerWidth >= 1024;
      this.picker.style.display = isDesktop ? "flex" : "none";
    };

    this._applyAtcLabel = (available) => {
      if (!this.atcLabel) return;

      if (!available) {
        this.atcLabel.textContent = "Sold Out";
        return;
      }

      const isDesktop = window.innerWidth >= 1024;
      const sellingPlanInput = this.querySelector(
        "[data-selling-plan-id-input]"
      );

      if (sellingPlanInput) {
        this.atcLabel.textContent = isDesktop ? "Add Subscription" : "Add";
        return;
      }

      this.atcLabel.textContent = isDesktop ? "Add to Bag" : "Add";
    };

    this._applyPickerVisibility();
    this._onResize = () => {
      this._applyPickerVisibility();
      const available =
        this._activeAvailable ?? (this.atcBtn ? !this.atcBtn.disabled : true);
      this._applyAtcLabel(available);
    };
    window.addEventListener("resize", this._onResize);

    this.picker.style.margin = "0";
    this.picker.style.padding = "0";
    this.picker.style.gap = "0";

    this.btns.forEach((btn) => {
      btn.style.margin = "0";
      btn.style.padding = "0.5rem";
      btn.style.borderRadius = "0";
      btn.style.borderLeft = "0";
      btn.style.borderRight = "0";
      btn.style.borderBottom = "0";
      btn.style.borderTop = "1px solid transparent";
      btn.style.cursor = "pointer";
      btn.style.background = "white";

      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setActive(btn);
      });
    });

    const initial =
      this.btns.find((b) => b.getAttribute("aria-pressed") === "true") ||
      this.btns[0];

    if (initial) this.setActive(initial);

    this.dataset.variantInit = "true";
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener("resize", this._onResize);
  }

  setActive(btn) {
    this.btns.forEach((b) => {
      b.setAttribute("aria-pressed", "false");
      b.style.borderTopColor = "transparent";
    });

    btn.setAttribute("aria-pressed", "true");
    btn.style.borderTopColor = "#000";

    const variantId = btn.dataset.variantId;
    if (variantId) this.idInput.value = variantId;

    const available = btn.dataset.variantAvailable === "true";
    this._activeAvailable = available;

    if (this.atcBtn) this.atcBtn.disabled = !available;
    if (this._applyAtcLabel) this._applyAtcLabel(available);

    const wrap = this.querySelector("[data-price-wrap]");
    const compareEl = wrap?.querySelector("[data-price-compare]");
    const priceEl = wrap?.querySelector("[data-price-active]");

    if (priceEl) {
      priceEl.dataset.priceNonmember = btn.dataset.priceNonmember || "0";
      priceEl.dataset.priceMember =
        btn.dataset.priceMember || btn.dataset.priceNonmember || "0";
    }

    if (compareEl) {
      compareEl.dataset.compareNonmember = btn.dataset.compareNonmember || "0";
      compareEl.dataset.compareMember =
        btn.dataset.compareMember || btn.dataset.priceNonmember || "0";
    }

    applyPricingState();

    const showDiscount =
      window?.Bero?.hasBeromasterInCart || window?.Bero?.isRivoSubscriber;

    if (this.priceEl && btn.dataset.variantPrice) {
      const nextBase = btn.dataset.variantPrice;
      if (this.priceEl.tagName === "PRICE-DISPLAY") {
        this.priceEl.dataset.priceBase = nextBase;
        this.priceEl.basePrice = Number(nextBase) || 0;
        this.priceEl.showDiscount = showDiscount;
        if (typeof this.priceEl.renderDiscount === "function")
          this.priceEl.renderDiscount();
      } else {
        this.priceEl.textContent = nextBase;
      }
    }

    if (this.compareEl) {
      const compare = (btn.dataset.variantCompare || "").trim();
      if (compare) {
        if (this.compareEl.tagName === "PRICE-DISPLAY") {
          this.compareEl.dataset.priceBase = compare;
          this.compareEl.basePrice = Number(compare) || 0;
          this.compareEl.showDiscount = showDiscount;
          if (typeof this.compareEl.renderDiscount === "function")
            this.compareEl.renderDiscount();
          this.compareEl.style.display = "inline";
        } else {
          this.compareEl.textContent = compare;
          this.compareEl.style.display = "inline";
        }
      } else {
        if (this.compareEl.tagName === "PRICE-DISPLAY") {
          this.compareEl.textContent = "";
          this.compareEl.style.display = "none";
        } else {
          this.compareEl.textContent = "";
          this.compareEl.style.display = "none";
        }
      }
    }
  }
}

customElements.define("product-item", ProductItem);
